import { useEffect, useState } from 'react';
import type { AdminVatRule, AdminVatRulesImportResult } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';

// Sistem → ƏDV qaydaları (KT-69, edv-qaydalari 2.8): XİF MN prefiksi → güzəşt (azad, k/t, 0%). Yalnız əsas admin.
// CSV əvvəl yoxlanır (heç nə yazılmır), sonra «Yüklə». Silmə yoxdur — qaydanı bitirmək üçün ends_on yazılır.

const MODE: Record<AdminVatRule['vatMode'], string> = { exempt: 'Azad', agri: 'K/t', vat0: '0%' };
const SAMPLE = 'prefix;vat_mode;scope;legal_basis;starts_on;ends_on;source_url;confidence;title\n3105;exempt;both;NK 252;2024-01-01;;https://e-qanun.az/…;list;Mineral gübrələr';

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

const ddmmyyyy = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;

export function VatRules() {
  const [rules, setRules] = useState<AdminVatRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [csv, setCsv] = useState('');
  const [check, setCheck] = useState<AdminVatRulesImportResult | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  function load() {
    adminApi
      .vatRules()
      .then(setRules)
      .catch((e: unknown) => setError(errText(e)));
  }
  useEffect(load, []);

  async function run(dryRun: boolean) {
    setBusy(true);
    setMsg(null);
    setError(null);
    try {
      const r = await adminApi.vatRulesImport(csv, dryRun);
      setCheck(r);
      if (r.applied) {
        setMsg(`Yazıldı: ${r.inserted} yeni, ${r.updated} yeniləndi.`);
        setCsv('');
        setCheck(null);
        load();
      }
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  const today = new Date(Date.now() + 4 * 3600_000).toISOString().slice(0, 10);
  const shown = (rules ?? []).filter((r) => !q.trim() || r.prefix.startsWith(q.replace(/[\s.]/g, '')) || (r.title ?? '').toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <>
      <section className="card card-wide">
        <h2>ƏDV qaydaları (XİF MN)</h2>
        <p className="muted small">Kodun ən uzun uyğun prefiksi götürülür; qayda yoxdursa mal ƏDV-lidir (18%). Mal avtomatik dəyişmir — obyektdə xəbərdarlıq görünür.</p>
        <label className="field">
          <span>Axtar</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Prefiks və ya ad" />
        </label>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {rules === null && !error && <p className="muted">Yüklənir…</p>}
        {rules && (
          <div className="table-wrap">
            <table className="data-table" aria-label="ƏDV qaydaları">
              <thead>
                <tr>
                  <th scope="col">Prefiks</th>
                  <th scope="col">Ad</th>
                  <th scope="col">Növ</th>
                  <th scope="col">Əsas</th>
                  <th scope="col">Qüvvədə</th>
                  <th scope="col">Dəqiqlik</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const ended = r.endsOn !== null && r.endsOn < today;
                  return (
                    <tr key={r.id}>
                      <td className="mono">{r.prefix}</td>
                      <td>{r.title ?? '—'}</td>
                      <td>{MODE[r.vatMode]}</td>
                      <td>{r.sourceUrl ? <a href={r.sourceUrl} target="_blank" rel="noreferrer noopener">{r.legalBasis}</a> : r.legalBasis}</td>
                      <td>
                        {ddmmyyyy(r.startsOn)} — {r.endsOn ? ddmmyyyy(r.endsOn) : 'müddətsiz'} {ended && <span className="badge badge-off">Bitib</span>}
                      </td>
                      <td>{r.confidence === 'list' ? 'Siyahı' : 'Qrup'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="card card-wide">
        <h2>CSV ilə yüklə</h2>
        <p className="muted small">
          Ayırıcı «;» və ya «,». Eyni prefix + starts_on olan qayda yenilənir. Mənbə: rəsmi sənəd (e-qanun.az); kodu bilinməyən sətir yazmayın.
        </p>
        <pre className="small mono">{SAMPLE}</pre>
        <label className="field">
          <span>CSV faylı</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              setCheck(null);
              if (f) void f.text().then(setCsv);
            }}
          />
        </label>
        <label className="field">
          <span>və ya mətn</span>
          <textarea
            rows={6}
            value={csv}
            onChange={(e) => {
              setCsv(e.target.value);
              setCheck(null);
            }}
            className="mono"
          />
        </label>
        {check && (
          <div role="status">
            {check.errors.length === 0 ? (
              <p className="ok">{check.rows} sətir yoxlamadan keçdi.</p>
            ) : (
              <>
                <p className="bad">Xəta var — heç nə yazılmayacaq:</p>
                <ul className="small">
                  {check.errors.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
        {msg && (
          <p className="ok" role="status">
            {msg}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn-secondary" disabled={busy || !csv.trim()} onClick={() => void run(true)}>
            Yoxla
          </button>
          <button type="button" className="btn-primary" disabled={busy || !check || check.errors.length > 0 || check.rows === 0} onClick={() => void run(false)}>
            Yüklə
          </button>
        </div>
      </section>
    </>
  );
}
